import React, { useState, useEffect } from "react";
import { AlertTriangle, Play, CheckCircle, AlertCircle, ExternalLink, Globe, Activity, Server, Shield } from "lucide-react";
import axios from "axios";
import { API_BASE_URL, API_ENDPOINTS, REQUEST_CONFIG } from "../config";
import CodeQualityMetrics from "./CodeQualityMetrics";
import CodeSecurityMetrics from "./CodeSecurityMetrics";

/**
 * CodeOverview component displays code security actions.
 * Provides options to trigger security pipelines.
 */
export default function CodeOverview() {
  const [pipelineStatus, setPipelineStatus] = useState(null); // null, 'loading', 'success', 'error'
  const [activePipeline, setActivePipeline] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dastReport, setDastReport] = useState(null);
  const [testReport, setTestReport] = useState(null);
  
  // Fetch the current pipeline status when component mounts
  useEffect(() => {
    // Initial check for pipeline status
    checkPipelineStatus();
    fetchDastReport();
    fetchTestReport();
    
    // Only set up polling if we found an active pipeline
    const setupPolling = (hasActivePipeline) => {
      // Clear any existing interval
      if (window.pipelineStatusInterval) {
        clearInterval(window.pipelineStatusInterval);
        window.pipelineStatusInterval = null;
      }
      
      // If we have an active pipeline, start polling
      if (hasActivePipeline) {
        console.log('Setting up polling for active pipeline');
        window.pipelineStatusInterval = setInterval(checkPipelineStatus, 10000);
      }
    };
    
    // Clean up interval on component unmount
    return () => {
      if (window.pipelineStatusInterval) {
        clearInterval(window.pipelineStatusInterval);
        window.pipelineStatusInterval = null;
      }
    };
  }, []);
  
  // Function to check the current pipeline status
  const checkPipelineStatus = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}${API_ENDPOINTS.CODE.PIPELINE_STATUS}`,
        { headers: REQUEST_CONFIG.getAuthHeaders() }
      );
      
      let hasActivePipeline = false;
      
      // If we have a pipeline with status running/pending/created, store it
      if (response.data && response.data.pipeline_id && 
          ['running', 'pending', 'created'].includes(response.data.status)) {
        setActivePipeline(response.data);
        hasActivePipeline = true;
      } else {
        setActivePipeline(null);
      }
      
      // Setup or clear polling based on whether we have an active pipeline
      if (window.pipelineStatusInterval && !hasActivePipeline) {
        // If we have polling but no active pipeline, stop polling
        console.log('Stopping pipeline status polling - no active pipeline');
        clearInterval(window.pipelineStatusInterval);
        window.pipelineStatusInterval = null;
      } else if (!window.pipelineStatusInterval && hasActivePipeline) {
        // If we don't have polling but found an active pipeline, start polling
        console.log('Starting pipeline status polling for active pipeline');
        window.pipelineStatusInterval = setInterval(checkPipelineStatus, 10000);
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error checking pipeline status:', error);
      setActivePipeline(null);
      setIsLoading(false);
      
      // Stop polling on error
      if (window.pipelineStatusInterval) {
        clearInterval(window.pipelineStatusInterval);
        window.pipelineStatusInterval = null;
      }
    }
  };
  
  const triggerSecurityPipeline = async () => {
    try {
      setPipelineStatus('loading');
      
      // Call the security API endpoint using the centralized configuration
      const response = await axios.post(
        `${API_BASE_URL}${API_ENDPOINTS.CODE.TRIGGER_PIPELINE}`,
        {}, // Request body (empty for now)
        { headers: REQUEST_CONFIG.getAuthHeaders() }
      );
      
      console.log('Pipeline triggered:', response.data);
      
      // If the response indicates a pipeline is already running, update our state
      if (response.data.status === 'info' && response.data.pipeline_id) {
        const pipelineData = {
          pipeline_id: response.data.pipeline_id,
          status: response.data.pipeline_status,
          web_url: response.data.web_url,
          triggered_at: response.data.triggered_at
        };
        setActivePipeline(pipelineData);
        setPipelineStatus('info');
        
        // Start polling since we now have an active pipeline
        if (!window.pipelineStatusInterval) {
          console.log('Starting pipeline status polling after trigger');
          window.pipelineStatusInterval = setInterval(checkPipelineStatus, 10000);
        }
      } else {
        // New pipeline was triggered
        setPipelineStatus('success');
        await checkPipelineStatus(); // Refresh the pipeline status
      }
      
      // Reset button status after 3 seconds (but keep active pipeline state)
      setTimeout(() => {
        setPipelineStatus(null);
      }, 3000);
      
    } catch (error) {
      console.error('Error triggering pipeline:', error);
      setPipelineStatus('error');
      
      // Reset status after 3 seconds
      setTimeout(() => {
        setPipelineStatus(null);
      }, 3000);
    }
  };
  
  // Function to fetch DAST report
  const fetchDastReport = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}${API_ENDPOINTS.CODE.DAST_REPORT}`,
        { headers: REQUEST_CONFIG.getAuthHeaders() }
      );
      
      if (response.data && response.data.status === 'success') {
        setDastReport(response.data);
      } else {
        setDastReport({
          status: 'error',
          message: response.data.message || 'Failed to fetch DAST report'
        });
      }
    } catch (error) {
      setDastReport({
        status: 'error',
        message: error.message || 'An error occurred'
      });
    }
  };
  
  // Function to fetch API Unit Test report
  const fetchTestReport = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}${API_ENDPOINTS.CODE.API_UNIT_TESTS_REPORT}`,
        { headers: REQUEST_CONFIG.getAuthHeaders() }
      );
      
      if (response.data && response.data.status === 'success') {
        // Parse XML content
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(response.data.report, "text/xml");
        
        setTestReport({
          ...response.data,
          parsedXml: xmlDoc
        });
      } else {
        setTestReport({
          status: 'error',
          message: response.data.message || 'Failed to fetch test report'
        });
      }
    } catch (error) {
      setTestReport({
        status: 'error',
        message: error.message || 'An error occurred'
      });
    }
  };
  
  // Determine if buttons should be disabled
  const isActionDisabled = isLoading || activePipeline !== null || pipelineStatus === 'loading';
  
  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-800">Code Security Analysis</h1>
      </div>
      
      {/* Active Pipeline Status */}
      {activePipeline && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <div className="flex items-center mb-2">
            <div className="p-2 rounded-full bg-blue-100 text-blue-600 mr-3">
              <Play size={18} />
            </div>
            <h3 className="text-lg font-semibold text-gray-800">Active Pipeline</h3>
          </div>
          <p className="text-gray-600 mb-3">A code security pipeline is currently running.</p>
          <div className="flex flex-wrap gap-2 text-sm text-gray-600">
            <div><span className="font-medium">Status:</span> {activePipeline.status}</div>
            <div><span className="font-medium">Pipeline ID:</span> {activePipeline.pipeline_id}</div>
            <div><span className="font-medium">Started:</span> {new Date(activePipeline.triggered_at).toLocaleString()}</div>
          </div>
          <a 
            href={activePipeline.web_url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="mt-3 inline-flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            View in GitLab <ExternalLink size={14} className="ml-1" />
          </a>
        </div>
      )}
      
      {/* Actions Card */}
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 mb-6">
        <div className="flex items-center mb-4">
          <div className="p-3 rounded-full bg-blue-50 text-blue-600 mr-4">
            <AlertTriangle size={24} />
          </div>
          <h2 className="text-xl font-semibold text-gray-800">Security Actions</h2>
        </div>
        <p className="text-gray-600 mb-6">
          {isActionDisabled 
            ? "Actions are disabled while a pipeline is running." 
            : "Trigger security scans for identified vulnerabilities."}
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4">
          <button 
            onClick={triggerSecurityPipeline}
            disabled={isActionDisabled}
            className={`flex items-center justify-center px-4 py-2 ${isActionDisabled ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} text-white rounded-lg transition-colors text-sm font-medium`}
          >
            {pipelineStatus === 'loading' ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </>
            ) : pipelineStatus === 'success' ? (
              <>
                <CheckCircle size={16} className="mr-2" />
                Pipeline Triggered
              </>
            ) : pipelineStatus === 'info' ? (
              <>
                <AlertCircle size={16} className="mr-2" />
                Pipeline Already Running
              </>
            ) : pipelineStatus === 'error' ? (
              <>
                <AlertCircle size={16} className="mr-2" />
                Failed to Trigger
              </>
            ) : (
              <>
                <Play size={16} className="mr-2" />
                Trigger Code Security Pipeline
              </>
            )}
          </button>
        </div>
      </div>
      
      {/* Security Metrics - SAST and Secret Detection */}
      <h2 className="text-2xl font-semibold mb-4 text-gray-800">Security Analysis</h2>
      <CodeSecurityMetrics />
      
      {/* Code Quality Metrics */}
      <h2 className="text-2xl font-semibold mb-4 text-gray-800">Code Quality</h2>
      <CodeQualityMetrics />
      
      {/* Application Analysis Summary */}
      <h2 className="text-2xl font-semibold mb-4 mt-8 text-gray-800">Application Analysis</h2>
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-50 text-purple-600 mr-4">
              <Globe size={24} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-800">DAST Vulnerabilities</h2>
              <p className="text-sm text-gray-500">Dynamic Application Security Testing</p>
            </div>
          </div>
          <button 
            onClick={() => window.location.href = '#/application-analysis'}
            className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 flex items-center"
          >
            View Details <ExternalLink size={14} className="ml-1" />
          </button>
        </div>
        
        {dastReport?.status === 'success' && dastReport.report ? (
          <div>
            {(() => {
              // Calculate stats from DAST report
              const vulns = dastReport.report.vulnerabilities || [];
              const total = vulns.length;
              let high = 0, medium = 0, low = 0, info = 0;
              
              vulns.forEach(vuln => {
                if (vuln.severity) {
                  const severity = vuln.severity.toLowerCase();
                  if (severity === 'critical' || severity === 'high') {
                    high++;
                  } else if (severity === 'medium') {
                    medium++;
                  } else if (severity === 'low') {
                    low++;
                  } else {
                    info++;
                  }
                }
              });
              
              return (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-gray-800">{total}</div>
                    <div className="text-sm text-gray-500">Total</div>
                  </div>
                  <div className="bg-red-50 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-red-600">{high}</div>
                    <div className="text-sm text-red-600">High</div>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-orange-600">{medium}</div>
                    <div className="text-sm text-orange-600">Medium</div>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-yellow-600">{low}</div>
                    <div className="text-sm text-yellow-600">Low</div>
                  </div>
                </div>
              );
            })()} 
          </div>
        ) : (
          <div className="text-center py-4 text-gray-500">
            {dastReport?.status === 'error' 
              ? `Error: ${dastReport.message}` 
              : 'No DAST data available. Run a security pipeline to get results.'}
          </div>
        )}
      </div>
      
      {/* API Testing Summary */}
      <h2 className="text-2xl font-semibold mb-4 text-gray-800">API Testing</h2>
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-50 text-green-600 mr-4">
              <Activity size={24} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-800">API Unit Tests</h2>
              <p className="text-sm text-gray-500">Backend API Test Results</p>
            </div>
          </div>
          <button 
            onClick={() => window.location.href = '#/api-testing'}
            className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 flex items-center"
          >
            View Details <ExternalLink size={14} className="ml-1" />
          </button>
        </div>
        
        {testReport?.status === 'success' && testReport.parsedXml ? (
          <div>
            {(() => {
              // Parse XML test report
              const parseTestStats = () => {
                try {
                  const testsuites = testReport.parsedXml.getElementsByTagName('testsuites')[0];
                  if (!testsuites) return { total: 0, failures: 0, passed: 0, time: 0 };
                  
                  const total = parseInt(testsuites.getAttribute('tests') || '0', 10);
                  const failures = parseInt(testsuites.getAttribute('failures') || '0', 10);
                  const errors = parseInt(testsuites.getAttribute('errors') || '0', 10);
                  const time = parseFloat(testsuites.getAttribute('time') || '0');
                  
                  return {
                    total,
                    failures: failures + errors,
                    passed: total - (failures + errors),
                    time
                  };
                } catch (error) {
                  console.error('Error parsing test stats:', error);
                  return { total: 0, failures: 0, passed: 0, time: 0 };
                }
              };
              
              const stats = parseTestStats();
              
              return (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-gray-800">{stats.total}</div>
                    <div className="text-sm text-gray-500">Total Tests</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-green-600">{stats.passed}</div>
                    <div className="text-sm text-green-600">Passed</div>
                  </div>
                  <div className="bg-red-50 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-red-600">{stats.failures}</div>
                    <div className="text-sm text-red-600">Failed</div>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-blue-600">{stats.time.toFixed(2)}s</div>
                    <div className="text-sm text-blue-600">Duration</div>
                  </div>
                </div>
              );
            })()}
          </div>
        ) : (
          <div className="text-center py-4 text-gray-500">
            {testReport?.status === 'error' 
              ? `Error: ${testReport.message}` 
              : 'No test data available. Run a security pipeline to get results.'}
          </div>
        )}
      </div>
    </div>
  );
}
